
import { useState } from "react";
import { Home, BarChart3, Briefcase, Search, Brain, Wrench, LogOut, Lock, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { SidebarSubMenu } from "./SidebarSubMenu";
import {
  marketingBusinessItems,
  seoItems,
  productivityItems,
  utilitiesItems,
  accountItems,
  adminMenuItems,
} from "@/components/sidebar/menu-items";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";

const categories = [
  { id: "home", label: "Accueil", icon: Home, items: accountItems, url: "/welcome" },
  { id: "dashboard", label: "Tableau de bord", icon: BarChart3, url: "/dashboard", premium: true },
  { id: "reports", label: "Mes rapports", icon: FileText, url: "/mes-rapports", premium: true },
  { id: "marketing", label: "Marketing & Business", icon: Briefcase, items: marketingBusinessItems },
  { id: "seo", label: "SEO & Visibilité", icon: Search, items: seoItems },
  { id: "productivity", label: "Productivité", icon: Brain, items: productivityItems },
  { id: "utilities", label: "Utilitaires", icon: Wrench, items: utilitiesItems },
];

export function SidebarMain() {
  const { signOut, user } = useAuth();
  const isMobileOrTabletPortrait = useIsMobile(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  
  const showAdminItems = user?.email === "babotesto@gmail.com";

  const handleCategoryClick = (id: string, url?: string, premium?: boolean) => {
    if (url) {
      // Si c'est premium et que l'utilisateur n'est pas connecté, on n'ouvre pas l'URL
      if (premium && !user) {
        return;
      }
      setActiveCategory(null);
      return;
    }
    
    if (activeCategory === id) {
      setActiveCategory(null);
    } else {
      setActiveCategory(id);
    }
  };

  return (
    <div className="flex h-full">
      <div className="w-full bg-[#23486a] text-white flex flex-col py-4 h-full">
        {categories.map((cat) => {
          const isPremium = cat.premium;
          const isDisabled = isPremium && !user;
          
          return (
            <a
              key={cat.id}
              href={isDisabled ? "#" : cat.url}
              className={cn(
                "flex items-center gap-3 px-6 py-3 text-left hover:bg-[#1e3a57] transition",
                activeCategory === cat.id && "bg-[#1a324d]",
                isDisabled && "cursor-not-allowed opacity-70"
              )}
              onClick={(e) => {
                if (isDisabled) {
                  e.preventDefault();
                  return;
                }
                
                if (!cat.url) {
                  e.preventDefault();
                  handleCategoryClick(cat.id, cat.url, cat.premium);
                }
              }}
            >
              <cat.icon className="w-5 h-5" />
              <span className="font-medium text-sm">{cat.label}</span>
              {isPremium && <Lock className="h-4 w-4 text-yellow-400 ml-2" />}
            </a>
          );
        })}
        
        {showAdminItems && (
          <a
            href="#"
            className={cn(
              "flex items-center gap-3 px-6 py-3 text-left hover:bg-[#1e3a57] transition",
              activeCategory === "admin" && "bg-[#1a324d]"
            )}
            onClick={(e) => {
              e.preventDefault();
              handleCategoryClick("admin");
            }}
          >
            <Wrench className="w-5 h-5" />
            <span className="font-medium text-sm">ADMINISTRATION</span>
          </a>
        )}
        
        <div className="mt-auto">
          <button
            onClick={signOut}
            className="flex items-center gap-3 px-6 py-3 text-left hover:bg-[#1e3a57] transition w-full text-red-500"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium text-sm">Déconnexion</span>
          </button>
        </div>
      </div>

      {activeCategory && (
        <SidebarSubMenu
          items={
            activeCategory === "admin" 
              ? adminMenuItems 
              : categories.find(c => c.id === activeCategory)?.items || []
          }
          title={
            activeCategory === "admin" 
              ? "ADMINISTRATION" 
              : categories.find(c => c.id === activeCategory)?.label || ""
          }
          onClose={() => setActiveCategory(null)}
          isMobile={isMobileOrTabletPortrait}
        />
      )}
    </div>
  );
}
